import { useMutation } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { UPDATE_MICROFINANCE } from "apollo/mutuations";
import { customLoader } from "components/utilities/loader";
import { useEffect } from "react";

const MicrofinanceEditModal = ({ setOpenedEdit, editData }) => {
  const { height } = useViewportSize();

  // Apollo Mutation
  const [editMicrofinance, { loading: updateMicrofinanceLoading }] =
    useMutation(UPDATE_MICROFINANCE);
  console.log("Edit Data:", editData);
  const form = useForm({
    initialValues: {
      name: "",
      phone_number: "",
      max_loan_amount: null,
      interest_rate: null,
      loan_repayment_duration: null,
      loan_repayment_duration_type: "MONTH",
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name is too short" : null),
      phone_number: (value) =>
        value.length < 10 ? "Invalid phone number" : null,
      loan_repayment_duration: (value) =>
        value <= 0 ? "Duration must be greater than 0" : null,
    },
  });

  const handleNonNegativeChange =
    (field, minimum = 0) =>
    (value) => {
      form.setFieldValue(
        field,
        typeof value === "number" ? Math.max(minimum, value) : value,
      );
    };

  const preventNegativeInput = (event) => {
    if (event.key === "-") {
      event.preventDefault();
    }
  };

  // Sync editData to form values
  useEffect(() => {
    if (editData) {
      form.setValues({
        name: editData.name || "",
        phone_number: editData.phone_number || "",
        max_loan_amount: editData.max_loan_amount || 0,
        interest_rate: editData.interest_rate || 0,
        loan_repayment_duration: editData.loan_repayment_duration || 1,
        loan_repayment_duration_type:
          editData.loan_repayment_duration_type || "MONTH",
      });
    }
  }, [editData]);

  const submit = (values) => {
    editMicrofinance({
      variables: {
        id: editData.id,
        name: values.name,
        phone_number: values.phone_number,
        max_loan_amount: parseFloat(values.max_loan_amount),
        interest_rate: parseFloat(values.interest_rate),
        loan_repayment_duration: parseInt(values.loan_repayment_duration),
        loan_repayment_duration_type: values.loan_repayment_duration_type,
      },
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "Microfinance Record Updated Successfully",
        });
        setOpenedEdit(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to update record",
        });
      },
    });
  };

  return (
    <>
      <LoadingOverlay
        visible={updateMicrofinanceLoading}
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.2 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(submit)}>
          <Stack p="md">
            <TextInput
              required
              label="Organization Name"
              placeholder="Enter name"
              {...form.getInputProps("name")}
            />

            <TextInput
              required
              label="Phone Number"
              placeholder="+254..."
              {...form.getInputProps("phone_number")}
            />

            <Grid>
              <Grid.Col span={6}>
                <NumberInput
                  required
                  label="Max Loan Amount"
                  min={0}
                  {...form.getInputProps("max_loan_amount")}
                  onChange={handleNonNegativeChange("max_loan_amount")}
                  onKeyDown={preventNegativeInput}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  required
                  label="Interest Rate (%)"
                  min={0}
                  {...form.getInputProps("interest_rate")}
                  onChange={handleNonNegativeChange("interest_rate")}
                  onKeyDown={preventNegativeInput}
                />
              </Grid.Col>
            </Grid>

            {/* New Duration Selection Fields */}
            <Grid align="flex-end">
              <Grid.Col span={6}>
                <Select
                  required
                  label="Duration Unit"
                  data={[
                    { value: "DAY", label: "Day(s)" },
                    { value: "WEEK", label: "Week(s)" },
                    { value: "MONTH", label: "Month(s)" },
                    { value: "YEAR", label: "Year(s)" },
                  ]}
                  {...form.getInputProps("loan_repayment_duration_type")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  required
                  label="Repayment Duration"
                  min={1}
                  {...form.getInputProps("loan_repayment_duration")}
                  onChange={handleNonNegativeChange(
                    "loan_repayment_duration",
                    1,
                  )}
                  onKeyDown={preventNegativeInput}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={12}>
                <Button
                  style={{
                    marginTop: "10px",
                    width: "30%",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  fullWidth
                  type="submit"
                >
                  Update Record
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};

export default MicrofinanceEditModal;
